import React from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FiActivity, FiHeart, FiAlertCircle, FiCheck, FiTarget, 
  FiUsers, FiClock, FiStar, FiArrowRight, FiShield 
} from 'react-icons/fi';
import TableOfContents from '../components/TableOfContents';

const DiseaseManagement = () => {
  const diseaseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Clinical Nutrition & Disease Management',
    'description': 'Personalized clinical diet programs designed to manage and reverse chronic conditions like PCOS, Thyroid disorders, Diabetes, and Hypertension under expert dietitian care.',
    'provider': {
      '@type': 'Organization',
      'name': 'Dfitclub',
      'url': 'https://dfitclub.com'
    },
    'offers': {
      '@type': 'Offer',
      'priceCurrency': 'INR'
    }
  };

  useSEO({
    title: 'Clinical Nutrition & Disease Management',
    description: 'Manage and reverse health conditions like PCOS, Thyroid, Diabetes, Cholesterol, and Uric Acid with clinical nutrition plans designed by expert dietitian Deeksha Rajput.',
    keywords: 'PCOS diet plan, Thyroid management nutrition, diabetes reversing diet, clinical nutritionist India, Dfitclub disease management',
    schema: diseaseSchema
  });

  const [heroRef, heroInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [conditionsRef, conditionsInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [approachRef, approachInView] = useInView({ threshold: 0.1, triggerOnce: true });

  const tocSections = [
    { id: 'hero', title: 'Disease Management' },
    { id: 'conditions', title: 'Conditions We Manage' },
    { id: 'approach', title: 'Our Approach' },
    { id: 'why-choose-us', title: 'Why Choose Us' },
    { id: 'get-started', title: 'Get Started' }
  ];

  const conditions = [
    {
      title: 'PCOS (Polycystic Ovary Syndrome)',
      description: 'A hormonal disorder affecting women of reproductive age, characterized by irregular periods and elevated male hormones.',
      symptoms: [
        'Irregular or missed periods',
        'Excessive hair growth (hirsutism)',
        'Acne and oily skin',
        'Weight gain or difficulty losing weight',
        'Hair thinning or male-pattern baldness',
        'Insulin resistance',
        'Mood changes and depression',
        'Fertility issues'
      ],
      nutritionHelp: [
        'Anti-inflammatory diet to reduce inflammation',
        'Low glycemic index foods to manage insulin',
        'Omega-3 rich foods for hormone balance',
        'Spearmint tea to reduce androgen levels',
        'High fiber foods for better insulin sensitivity'
      ],
      icon: FiActivity,
      color: 'bg-pink-50 border-pink-200'
    },
    {
      title: 'PCOD (Polycystic Ovarian Disease)',
      description: 'A condition where ovaries produce immature or partially mature eggs in large numbers, leading to cysts.',
      symptoms: [
        'Irregular menstrual cycles',
        'Weight gain, especially around the waist',
        'Acne and skin darkening',
        'Hair loss or thinning',
        'Excessive facial and body hair',
        'Mood swings and depression',
        'High cholesterol levels',
        'Sleep apnea'
      ],
      nutritionHelp: [
        'Balanced macronutrient ratios',
        'Complex carbohydrates over simple sugars',
        'Lean proteins for hormone production',
        'Healthy fats from nuts and seeds',
        'Regular meal timing to stabilize hormones'
      ],
      icon: FiHeart,
      color: 'bg-purple-50 border-purple-200'
    },
    {
      title: 'Thyroid Disorders',
      description: 'Conditions affecting the thyroid gland, including hypothyroidism (underactive) and hyperthyroidism (overactive).',
      symptoms: [
        'Unexplained weight gain or loss',
        'Fatigue and weakness',
        'Hair loss or thinning',
        'Dry skin and hair',
        'Cold or heat intolerance',
        'Mood changes (depression/anxiety)',
        'Memory problems',
        'Irregular heart rate',
        'Constipation or diarrhea'
      ],
      nutritionHelp: [
        'Iodine-rich foods for thyroid function',
        'Selenium and zinc for hormone conversion',
        'Avoiding goitrogenic foods when raw',
        'Gluten-free diet for autoimmune thyroid',
        'Anti-inflammatory foods to reduce antibodies'
      ],
      icon: FiShield,
      color: 'bg-blue-50 border-blue-200'
    },
    {
      title: 'Diabetes (Type 1 & Type 2)',
      description: 'A group of metabolic disorders characterized by high blood sugar levels over a prolonged period.',
      symptoms: [
        'Frequent urination (polyuria)',
        'Excessive thirst (polydipsia)',
        'Unexplained weight loss',
        'Increased hunger',
        'Fatigue and weakness',
        'Blurred vision',
        'Slow-healing wounds',
        'Frequent infections',
        'Numbness in hands or feet'
      ],
      nutritionHelp: [
        'Low glycemic index diet plans',
        'Portion control and carb counting',
        'High fiber foods for blood sugar control',
        'Chromium and magnesium supplementation',
        'Regular meal timing to prevent spikes'
      ],
      icon: FiTarget,
      color: 'bg-green-50 border-green-200'
    }
  ];

  const approachPoints = [
    {
      icon: FiUsers,
      title: 'Personalized Assessment',
      description: 'Comprehensive health evaluation and condition-specific nutritional analysis'
    },
    {
      icon: FiTarget,
      title: 'Targeted Nutrition Plans',
      description: 'Customized meal plans designed specifically for your condition and symptoms'
    },
    {
      icon: FiClock,
      title: 'Regular Monitoring',
      description: 'Continuous tracking of symptoms, biomarkers, and progress adjustments'
    },
    {
      icon: FiStar,
      title: 'Expert Guidance',
      description: 'Ongoing support from certified nutritionists specializing in disease management'
    }
  ];

  const whyChooseUs = [
    'Specialized expertise in hormonal and metabolic disorders',
    'Evidence-based nutrition protocols',
    'Collaboration with healthcare providers',
    'Personalized supplement recommendations',
    'Lifestyle modification guidance',
    '24/7 support for health concerns'
  ];

  return (
    <div className="min-h-screen">
      <TableOfContents sections={tocSections} />
      
      {/* Hero Section */}
      <section id="hero" ref={heroRef} className="relative min-h-[50vh] flex items-center justify-center hero-gradient overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-72 h-72 bg-primary-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="heading-hero text-gray-900 mb-4">
              <span className="text-gray-900">Disease</span> <span className="text-visible-orange">Management</span>
            </h1>
            <p className="text-base text-gray-600 leading-relaxed mb-6">
              Specialized nutrition therapy for PCOS, PCOD, Thyroid disorders, and Diabetes
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Enroll Now
              </Link>
              <a href="#conditions" className="btn-secondary text-lg px-8 py-4">
                Learn More
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Conditions Section */}
      <section id="conditions" ref={conditionsRef} className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={conditionsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-3">
              Conditions We <span className="text-visible-orange">Specialize In</span>
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Understanding your condition is the first step towards effective management through nutrition
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {conditions.map((condition, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={conditionsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className={`rounded-lg border-2 p-4 ${condition.color} hover:shadow-lg transition-all duration-300`}
              >
                <div className="flex items-center mb-3">
                  <condition.icon className="w-6 h-6 text-primary-600 mr-2" />
                  <h3 className="text-lg font-bold text-secondary-900">{condition.title}</h3>
                </div>
                
                <p className="text-secondary-700 mb-4 leading-relaxed text-xs">
                  {condition.description}
                </p>

                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-secondary-900 mb-1.5 flex items-center">
                    <FiAlertCircle className="w-3.5 h-3.5 mr-1.5 text-orange-500" />
                    Common Symptoms
                  </h4>
                  <ul className="space-y-0.5">
                    {condition.symptoms.map((symptom, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="w-1 h-1 bg-red-400 rounded-full mt-1.5 mr-1.5 flex-shrink-0"></span>
                        <span className="text-secondary-600 text-xs leading-tight">{symptom}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-secondary-900 mb-1.5 flex items-center">
                    <FiCheck className="w-3.5 h-3.5 mr-1.5 text-green-500" />
                    How Nutrition Helps
                  </h4>
                  <ul className="space-y-0.5">
                    {condition.nutritionHelp.map((help, idx) => (
                      <li key={idx} className="flex items-start">
                        <FiCheck className="w-3 h-3 text-green-500 mt-1 mr-1.5 flex-shrink-0" />
                        <span className="text-secondary-600 text-xs leading-tight">{help}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section id="approach" ref={approachRef} className="py-10 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={approachInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-3">
              Our <span className="text-visible-orange">Approach</span>
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              A comprehensive, science-based approach to managing your condition through nutrition
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {approachPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={approachInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white rounded-xl p-4 text-center shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <point.icon className="w-10 h-10 text-primary-600 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">{point.title}</h3>
                <p className="text-secondary-600 text-sm">{point.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-choose-us" className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={approachInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-center mb-8"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-3">
                Why Choose <span className="text-visible-orange">Our Disease Management Program</span>
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-4">
              {whyChooseUs.map((reason, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={approachInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="flex items-center p-3 bg-gray-50 rounded-lg"
                >
                  <FiCheck className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-secondary-700 text-sm">{reason}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-started" className="py-12 bg-gradient-to-r from-primary-100 to-primary-200 text-neutral-800">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={approachInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Take Control of Your Health?
            </h2>
            <p className="text-base text-gray-600 leading-relaxed mb-6">
              Start your journey towards better health with our specialized disease management program
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-accent">
                Enroll Now
              </Link>
              <Link to="/services" className="btn-secondary">
                View All Services
                <FiArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DiseaseManagement;